import cls from "classnames";
import BookingFormHorizontal from "components/BookingFormHorizontal";
import BookingFormVertical from "components/BookingFormVertical";
import ConditionalLink from "components/ConditionalLink";
import AddonButton from "components/Design/Button/AddonButton";
import DrawerHead from "components/Design/Card/DrawerHead";
import PageContext from "contexts/PageContext";
import dayjs from "dayjs";
import { useViewportScroll } from "framer-motion";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { useTranslation } from "gatsby-plugin-react-i18next";
import useBookingCode from "hooks/useBookingCode";
import useBookingFormLink from "hooks/useBookingFormLink";
import useDrawer from "hooks/useDrawer";
import useQueryParams from "hooks/useQueryParams";
import React, { useContext, useEffect, useState } from "react";
import { ISO_FORMAT } from "store/constants/date";
import { PACKAGE } from "store/constants/group";
import { JAKARTA, JAKARTA_LOCATION, KOMODO } from "store/constants/hotel";
import { QUARANTINE } from "store/constants/tag";
import { getDiffNights } from "utils/dates";
import { renderForMobile } from "utils/renderForMobile";
import Drawer from "./Drawer";
import Cookies from "js-cookie";
import "./style.less";

const RoomSelectionSearchDrawer = ({
  cta,
  destination,
  defaultOpen,
  location,
  hotelCode: hotelCodeFromProps,
  minimumStay,
  bookingCode: bookingCodeFromProps,
  stayPeriod,
  isPackage,
  packageCode,
  hotelId,
  roomId,
  minLeadDay,
}) => {
  const { t } = useTranslation(["homepage", "bookingForm"]);
  const { isOpen, open, close } = useDrawer();
  const breakpoints = useBreakpoint();
  const { hotel_code: hotelCodeFromUrl } = useQueryParams();

  const { roomDetail = {} } = useContext(PageContext);
  const [guest, setGuest] = useState({
    rooms: 1,
    adults: 2,
    children: 0,
  });
  const [startDate, setStartDate] = useState(
    stayPeriod &&
      dayjs(stayPeriod.start_date).isAfter(dayjs().format(ISO_FORMAT))
      ? dayjs(stayPeriod.start_date).format(ISO_FORMAT)
      : minLeadDay
      ? dayjs().add(minLeadDay, "day").format(ISO_FORMAT)
      : dayjs().format(ISO_FORMAT)
  );

  const [endDate, setEndDate] = useState(
    typeof minimumStay === "number"
      ? dayjs(startDate).add(minimumStay, "day").format(ISO_FORMAT)
      : dayjs(startDate).add(1, "day").format(ISO_FORMAT)
  );

  const [saveLabel, setSaveLabel] = useState(
    t("bookingForm:save_night", {
      count:
        typeof minimumStay === "number"
          ? minimumStay
          : getDiffNights(startDate, endDate),
    })
  );

  const [bookingCode, setBookingCode] = useBookingCode(bookingCodeFromProps);

  const setDateValues = (dates) => {
    if (dates.startDate && dates.endDate) {
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
    }
  };

  const setGuestCount = (updatedGuest) => {
    setGuest((guest) => ({
      ...guest,
      ...updatedGuest,
    }));
  };

  // when only startdate is selcted and endDate is not
  // set the no of nights to 1
  useEffect(() => {
    if (!dayjs(endDate).isValid()) {
      setSaveLabel(
        t("bookingForm:save_night", {
          count: 1,
        })
      );
    }
  }, [endDate]);

  const onDateChange = (dates) => {
    if (dates.startDate && dates.endDate) {
      const diffNights = dates.endDate?.diff(dates.startDate, "days");
      setSaveLabel(t("bookingForm:save_night", { count: diffNights }));
    }
  };

  const isJakartaLocation =
    location === JAKARTA || location === JAKARTA_LOCATION;

  const isGeolocationSG = Cookies.get("AYANA_COUNTRY_CODE") === "SG";
  const isGeolocationUS = Cookies.get("AYANA_COUNTRY_CODE") === "US";

  const hotelCode = hotelCodeFromProps || hotelCodeFromUrl;
  const { link, handleInvalidSearch } = useBookingFormLink({
    startDate,
    endDate,
    rooms: guest.rooms,
    adults: guest.adults,
    children: guest.children,
    hotelCode: hotelCode,
    roomCode: roomDetail.slug,
    location,
    bookingCode,
    tags: minimumStay === 9 ? [QUARANTINE] : [],
    group: isPackage && PACKAGE,
    packageCode: packageCode ? packageCode : undefined,
  });

  const vh = typeof window === "undefined" ? 0 : window.innerHeight;
  const stickyOffset = vh * 0.16;
  const [sticky, setSticky] = useState(false);
  const { scrollY } = useViewportScroll();
  scrollY.onChange((offsetY) => setSticky(offsetY > stickyOffset));

  const viewRoomsButton = (
    <AddonButton
      label={
        location?.toUpperCase() === KOMODO.toUpperCase() ||
        (location?.toUpperCase() === JAKARTA.toUpperCase() &&
          !isGeolocationSG &&
          !isGeolocationUS)
          ? t("View Rates")
          : t("bookingForm:jakarta_signup")
      }
      fullRounded
      onClick={handleInvalidSearch}
    />
  );

  useEffect(() => {
    if (defaultOpen) {
      setTimeout(() => open());
    }
  }, []);

  const jakartaLink = `https://be.synxis.com/?adult=${
    guest.adults
  }&arrive=${startDate}&chain=10237&child=${
    guest.children
  }&currency=IDR&current_lang=id&current_location=jakarta&depart=${endDate}&hotel=10526&language=en-US&level=hotel&locale=en-US&rooms=${
    guest.rooms
  }&type=room-reservations&promo=${
    bookingCode ? bookingCode?.toUpperCase() : ""
  }`;

  return (
    <div>
      {cta(open, sticky)}
      <Drawer
        placement={renderForMobile(breakpoints) ? "bottom" : "top"}
        visible={isOpen}
        className="drawer-container lg:shadow-booking-form-desktop room-selection-drawer"
        onClose={close}>
        <DrawerHead title={t("Book My Stay")} onClose={close} />
        <div
          className={cls(
            "w-full grid grid-cols-5 lg:grid-cols-12 gap-x-20 mx-auto px-20"
          )}>
          <div className="col-span-12 lg:mt-20">
            <div className="pb-20">
              <div className="pt-20 text-center font-mulish text-14-24 lg:hidden">
                {t("fill stay detail")}
              </div>

              <div className="block pt-24 pb-16 lg:hidden">
                <BookingFormVertical
                  guest={guest}
                  location={location}
                  destination={destination}
                  setDateValues={setDateValues}
                  setGuestCount={setGuestCount}
                  onDateChange={onDateChange}
                  buttonLabel={saveLabel}
                  endDate={endDate}
                  startDate={startDate}
                  bookingCode={bookingCode}
                  setBookingCode={setBookingCode}
                  destinationLabel={t("Destination")}
                  stayingDateLabel={t("stayingDate")}
                  bookingCodeLabel={t("bookingCode")}
                  bookingCodePlaceholder={t("bookingCodePlaceholder")}
                  hotelId={hotelId}
                  roomId={roomId}
                />
              </div>
              <div className="hidden lg:block">
                <BookingFormHorizontal
                  isPackage={isPackage}
                  stayPeriod={stayPeriod}
                  minimumStay={minimumStay}
                  bookingCode={bookingCodeFromProps}
                  location={location}
                  hotelCode={hotelCode}
                  roomCode={roomDetail.slug}
                  packageCode={packageCode}
                  hotelId={hotelId}
                  roomId={roomId}
                  minLeadDay={minLeadDay}
                />
              </div>

              <div className="grid col-span-2 lg:hidden gap-16">
                <ConditionalLink link={link} sameTab>
                  {viewRoomsButton}
                </ConditionalLink>

                {isJakartaLocation && (isGeolocationSG || isGeolocationUS) && (
                  <>
                    <div className="w-full h-1 bg-gray-med lg:w-1 lg:h-45" />
                    <a
                      href={jakartaLink}
                      rel="noopener noreferrer"
                      className="p-14 px-20 text-center uppercase border border-gold rounded-sm text-gold text-14-22 font-semibold self-stretch flex items-center justify-center font-mulish">
                      {t("View Rooms")}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

RoomSelectionSearchDrawer.defaultProps = {
  showImage: false,
};

export default RoomSelectionSearchDrawer;
